import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Plus, Pencil, Trash2, ShieldCheck, Shield, User, Loader2, RefreshCw } from "lucide-react";

const ALL_PERMS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "api_keys", label: "API Keys" },
  { key: "playground", label: "Playground" },
  { key: "agents", label: "Agents" },
  { key: "usage_history", label: "Usage History" },
  { key: "budget_settings", label: "Budget Settings" },
  { key: "user_management", label: "User Management" },
  { key: "api_management", label: "API Management" },
];

export default function UserManagementPage({ api }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "user", permissions: {} });
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${api}/users`);
      setUsers(res.data);
    } catch (err) {
      toast.error("Failed to load users");
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openCreate = () => {
    setEditUser(null);
    setForm({ email: "", name: "", password: "", role: "user", permissions: ALL_PERMS.reduce((a, p) => ({ ...a, [p.key]: true }), {}) });
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({ email: u.email, name: u.name || "", password: "", role: u.role, permissions: u.permissions || {} });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editUser) {
        const body = { name: form.name, role: form.role, permissions: form.permissions, enabled: editUser.enabled };
        if (form.password) body.password = form.password;
        await axios.put(`${api}/users/${editUser.id}`, body);
        toast.success("User updated");
      } else {
        if (!form.email || !form.password) { toast.error("Email and password required"); setSaving(false); return; }
        await axios.post(`${api}/users`, { ...form });
        toast.success("User created");
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save user");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${api}/users/${deleteId}`);
      toast.success("User deleted");
      setDeleteId(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const toggleEnabled = async (u) => {
    try {
      await axios.put(`${api}/users/${u.id}`, { enabled: !u.enabled });
      toast.success(u.enabled ? "User disabled" : "User enabled");
      fetchUsers();
    } catch (err) { toast.error("Failed to update"); }
  };

  const togglePerm = (key) => {
    setForm(f => ({ ...f, permissions: { ...f.permissions, [key]: !f.permissions[key] } }));
  };

  const roleIcon = (role) => {
    if (role === "superadmin") return <ShieldCheck className="w-3.5 h-3.5 text-primary" />;
    if (role === "admin") return <Shield className="w-3.5 h-3.5 text-amber-600" />;
    return <User className="w-3.5 h-3.5 text-muted-foreground" />;
  };

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold">User Management</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage user accounts and permissions</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchUsers}><RefreshCw className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Refresh</span></Button>
          <Button size="sm" onClick={openCreate} data-testid="create-user-btn"><Plus className="w-4 h-4 mr-1" /> Create User</Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>2FA</TableHead>
                <TableHead>Permissions</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></TableCell></TableRow>
              ) : users.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No users found</TableCell></TableRow>
              ) : users.map(u => (
                <TableRow key={u.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold">
                        {(u.name || u.email)[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{u.name || "--"}</p>
                        <p className="text-[11px] text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell><div className="flex items-center gap-1">{roleIcon(u.role)}<span className="text-xs capitalize">{u.role}</span></div></TableCell>
                  <TableCell>
                    <Badge variant={u.enabled !== false ? "default" : "destructive"} className="text-[10px]">
                      {u.enabled !== false ? "Active" : "Disabled"}
                    </Badge>
                  </TableCell>
                  <TableCell><span className="text-xs">{u.totp_enabled ? "Enabled" : "Off"}</span></TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-0.5">
                      {u.role === "superadmin" ? (
                        <Badge variant="outline" className="text-[9px]">All</Badge>
                      ) : ALL_PERMS.filter(p => u.permissions?.[p.key]).map(p => (
                        <Badge key={p.key} variant="outline" className="text-[9px]">{p.label}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell><span className="text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "--"}</span></TableCell>
                  <TableCell className="text-right">
                    {u.role !== "superadmin" && (
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => toggleEnabled(u)}>
                          <span className="text-[10px]">{u.enabled !== false ? "Disable" : "Enable"}</span>
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(u)}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => setDeleteId(u.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editUser ? "Edit User" : "Create User"}</DialogTitle>
            <DialogDescription>{editUser ? "Update user details and permissions" : "Add a new user to the platform"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!editUser && (
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="user@example.com" />
              </div>
            )}
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name" />
            </div>
            <div className="space-y-2">
              <Label>{editUser ? "New Password (leave blank to keep)" : "Password"}</Label>
              <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder={editUser ? "Leave blank" : "Min 6 characters"} />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Feature Permissions</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {ALL_PERMS.map(p => (
                  <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded border hover:bg-muted/50 transition-colors">
                    <input type="checkbox" checked={!!form.permissions[p.key]} onChange={() => togglePerm(p.key)} className="rounded" />
                    {p.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? "Saving..." : editUser ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete User?</DialogTitle><DialogDescription>This action cannot be undone.</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
